import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "../components/Topbar";
import { useAuth } from "../context/AuthContext";
import { listHotels } from "../api/hotels";
import DataUnavailableCard, { TECHNICAL_ERROR_MESSAGE } from "./analyticsDashboard/components/DataUnavailableCard";
import DemoAnalyticsBanner from "./analyticsDashboard/components/DemoAnalyticsBanner";
import DynamicsChart from "./analyticsDashboard/components/DynamicsChart";
import DynamicsTableCard from "./analyticsDashboard/components/DynamicsTableCard";
import FiltersSection from "./analyticsDashboard/components/FiltersSection";
import FiltersLoadingPlaceholder from "./analyticsDashboard/components/FiltersLoadingPlaceholder";
import GuestCountDisplay from "./analyticsDashboard/components/GuestCountDisplay";
import LoadingOverlay from "./analyticsDashboard/components/LoadingOverlay";
import ObjectSelectCard from "./analyticsDashboard/components/ObjectSelectCard";
import { DEFAULT_FILTERS, DEFAULT_FILTER_OPTIONS } from "./analyticsDashboard/constants";
import type { DynamicsData, FilterOption, FilterOptions } from "./analyticsDashboard/types";
import { getFilters, getPriceDynamics } from "./analyticsDashboard/api";
import {
  DEMO_ANALYTICS_GROUP_ID,
  DEMO_ANALYTICS_OBJECT_OPTION,
  DEMO_ANALYTICS_OBJECT_VALUE,
} from "./analyticsDashboard/demoObject";
import {
  LAST_PARSING_DATE_LABEL,
  isLastParsingDateInfoReady,
  useLastParsingDateInfo,
} from "./analyticsDashboard/hooks/useLastParsingDate";
import {
  isAvailableParsingDatesInfoReady,
  normalizeAvailableDateRange,
  useAvailableParsingDates,
} from "./analyticsDashboard/hooks/useAvailableParsingDates";
import { getSubscriptionState } from "../utils/subscription";
import {
  buildAnalyticsGuestsPayload,
  buildGuestSelectionOptions,
} from "../utils/guests";
import { loadHotelsDisplaySettings } from "../utils/hotelDisplaySettings";

const formatIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const getTodayDate = () => formatIsoDate(new Date());
const getTomorrowDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return formatIsoDate(date);
};
const getDateWithOffset = (isoDate: string, offset: number) => {
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  date.setDate(date.getDate() + offset);
  return formatIsoDate(date);
};
const formatDaysStayLabel = (value: number) => {
  if (value === 1) return "1 ночь";
  if (value < 5) return `${value} ночи`;
  return `${value} ночей`;
};
const getDefaultDate = () => getTomorrowDate();
const getDefaultInterval = (to = getTodayDate()) => ({ from: getDateWithOffset(to, -7), to });

export default function AnalyticsCompetitorTrendsPage() {
  const { user, company_id: companyId } = useAuth();
  const subscriptionState = getSubscriptionState(user?.subscription_expires_on);
  const isSubscriptionExpired = subscriptionState.isExpired;

  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    date: getDefaultDate(),
    dateIntervalFrom: getDefaultInterval().from,
    dateIntervalTo: getDefaultInterval().to,
  });
  const [daysStay, setDaysStay] = useState<number>(1);
  const [guests, setGuests] = useState<string>("2");
  const [appliedAt, setAppliedAt] = useState<string | null>(null);
  const [minDynamics, setMinDynamics] = useState<DynamicsData>({ dates: [], rows: [] });
  const [avgDynamics, setAvgDynamics] = useState<DynamicsData>({ dates: [], rows: [] });
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [checkInRange, setCheckInRange] = useState<{ from: string; to: string } | null>(null);
  const [hotelOptions, setHotelOptions] = useState<FilterOption[]>([]);
  const [hotelGroupMap, setHotelGroupMap] = useState<Record<string, number | null>>({});
  const [hotelMinStayMap, setHotelMinStayMap] = useState<Record<string, number[] | null>>({});
  const [hotelGuestsMap, setHotelGuestsMap] = useState<Record<string, number[] | null>>({});
  const [hotelGuestsChildMap, setHotelGuestsChildMap] = useState<Record<string, number[] | null>>({});
  const [baseHotelFilterNames, setBaseHotelFilterNames] = useState<string[]>([]);
  const [competitorHotelNameMap, setCompetitorHotelNameMap] = useState<Record<string, string>>({});
  const [selectedHotelHasCompetitors, setSelectedHotelHasCompetitors] = useState<boolean | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"min" | "avg">("min");
  const minDynamicsRequestIdRef = useRef(0);
  const avgDynamicsRequestIdRef = useRef(0);
  const lastDefaultIntervalRef = useRef<{ from: string; to: string } | null>({
    from: filters.dateIntervalFrom,
    to: filters.dateIntervalTo,
  });

  const selectedGroupId = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelGroupMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelGroupMap]);
  const selectedHotelMinStay = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelMinStayMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelMinStayMap]);
  const selectedHotelGuests = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelGuestsMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelGuestsMap]);
  const selectedHotelGuestsChild = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelGuestsChildMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelGuestsChildMap]);
  const selectedHotelMinStayOptions = selectedHotelMinStay ?? [];
  const selectedHotelGuestOptions = useMemo(
    () => buildGuestSelectionOptions(selectedHotelGuests, selectedHotelGuestsChild),
    [selectedHotelGuests, selectedHotelGuestsChild],
  );
  const hasSelectedHotelMinStay = Boolean(selectedHotelMinStay?.length);
  const hasSelectedHotelGuests = Boolean(filters.hotel);
  const hasHotelSelected = Boolean(filters.hotel);
  const isDemoAnalytics = filters.hotel === DEMO_ANALYTICS_OBJECT_VALUE;
  const shouldBlockAnalyticsRequests = hasHotelSelected && !isDemoAnalytics && selectedHotelHasCompetitors !== true;
  const lastParsingDate = useLastParsingDateInfo(selectedGroupId, daysStay, !isSubscriptionExpired);
  const isLastParsingDateReady = isLastParsingDateInfoReady(
    lastParsingDate,
    selectedGroupId,
    daysStay,
    !isSubscriptionExpired,
  );
  const availableParsingDates = useAvailableParsingDates(selectedGroupId, daysStay, guests, !isSubscriptionExpired);
  const isAvailableParsingDatesReady = isAvailableParsingDatesInfoReady(
    availableParsingDates,
    selectedGroupId,
    daysStay,
    guests,
    !isSubscriptionExpired,
  );
  const defaultCollectionInterval = getDefaultInterval(lastParsingDate.date ?? getTodayDate());
  const selectedCollectionInterval = {
    from: filters.dateIntervalFrom || defaultCollectionInterval.from,
    to: filters.dateIntervalTo || defaultCollectionInterval.to,
  };
  const shouldBlockParsingDateRequests = Boolean(selectedGroupId) && (
    !isAvailableParsingDatesReady ||
    Boolean(availableParsingDates.error) ||
    availableParsingDates.dates.length === 0 ||
    !availableParsingDates.dateSet.has(selectedCollectionInterval.from) ||
    !availableParsingDates.dateSet.has(selectedCollectionInterval.to) ||
    selectedCollectionInterval.to < selectedCollectionInterval.from
  );
  const filtersLoading = hasHotelSelected && (optionsLoading || availableParsingDates.loading);
  const previousDefaultInterval = lastDefaultIntervalRef.current;
  const isCurrentCollectionIntervalDefault =
    (!filters.dateIntervalFrom && !filters.dateIntervalTo) ||
    Boolean(
      previousDefaultInterval &&
        filters.dateIntervalFrom === previousDefaultInterval.from &&
        filters.dateIntervalTo === previousDefaultInterval.to,
    );
  const shouldWaitForDefaultInterval =
    isLastParsingDateReady &&
    Boolean(lastParsingDate.date) &&
    filters.dateIntervalTo !== lastParsingDate.date &&
    isCurrentCollectionIntervalDefault;

  const formatDateShort = (value: string) => {
    const [year, month, day] = value.split("-");
    if (!year || !month || !day) return value;
    return `${day}.${month}`;
  };
  const buildHotelFilterPayload = (currentFilters: typeof filters) => {
    if (!currentFilters.competitor.length) return undefined;
    return Array.from(
      new Set([
        ...baseHotelFilterNames,
        ...currentFilters.competitor.map((value) => competitorHotelNameMap[value] ?? value),
      ]),
    );
  };
  const buildBaseFiltersPayload = (currentFilters: typeof filters) => ({
    category: currentFilters.category.length ? currentFilters.category : undefined,
    ota: currentFilters.ota.length ? currentFilters.ota : undefined,
    board_type: currentFilters.meals.length ? currentFilters.meals : undefined,
    refund_policy: currentFilters.refund.length ? currentFilters.refund : undefined,
    payment_type: currentFilters.payment.length ? currentFilters.payment : undefined,
  });
  const buildDaysStayPayload = () => (daysStay > 1 ? { days_stay: daysStay } : {});
  const buildGuestsPayload = () => buildAnalyticsGuestsPayload(guests);

  useEffect(() => {
    if (!companyId) return;
    let isMounted = true;
    setOptionsLoading(true);

    listHotels(companyId, { page: 1, per_page: 200 })
      .then(async (hotelsResponse) => {
        if (!isMounted) return;
        const hotels = hotelsResponse.items.map((item) => ({ label: item.name, value: String(item.id) }));
        if (hotelsResponse.items.length === 0) {
          const defaults = getDefaultInterval();
          lastDefaultIntervalRef.current = defaults;
          setHotelGroupMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: DEMO_ANALYTICS_GROUP_ID });
          setHotelMinStayMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setHotelGuestsMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setHotelGuestsChildMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setHotelOptions([DEMO_ANALYTICS_OBJECT_OPTION]);
          setBaseHotelFilterNames([]);
          setCompetitorHotelNameMap({});
          setNoData(false);
          setError(null);
          setFilters({
            ...DEFAULT_FILTERS,
            hotel: DEMO_ANALYTICS_OBJECT_VALUE,
            date: getDefaultDate(),
            dateIntervalFrom: defaults.from,
            dateIntervalTo: defaults.to,
          });
          return;
        }
        const displaySettingsMap = await loadHotelsDisplaySettings(hotelsResponse.items);
        if (!isMounted) return;
        const groupMap: Record<string, number | null> = {};
        const minStayMap: Record<string, number[] | null> = {};
        const guestsMap: Record<string, number[] | null> = {};
        const guestsChildMap: Record<string, number[] | null> = {};
        hotelsResponse.items.forEach((item) => {
          const displaySettings = displaySettingsMap[String(item.id)];
          groupMap[String(item.id)] = item.group_id ?? null;
          minStayMap[String(item.id)] = displaySettings?.min_stay ?? item.min_stay ?? null;
          guestsMap[String(item.id)] = displaySettings?.guests ?? item.guests ?? null;
          guestsChildMap[String(item.id)] = displaySettings?.guests_child ?? item.guests_child ?? null;
        });
        setHotelGroupMap(groupMap);
        setHotelMinStayMap(minStayMap);
        setHotelGuestsMap(guestsMap);
        setHotelGuestsChildMap(guestsChildMap);
        setHotelOptions(hotels);
        setFilters((prev) => {
          if (hotelsResponse.items.length === 1) {
            const defaults = getDefaultInterval();
            lastDefaultIntervalRef.current = defaults;
            return {
              ...DEFAULT_FILTERS,
              hotel: String(hotelsResponse.items[0].id),
              date: getDefaultDate(),
              dateIntervalFrom: defaults.from,
              dateIntervalTo: defaults.to,
            };
          }
          if (hotels.some((item) => item.value === prev.hotel)) {
            return prev;
          }
          const defaults = getDefaultInterval();
          lastDefaultIntervalRef.current = defaults;
          return {
            ...DEFAULT_FILTERS,
            date: getDefaultDate(),
            dateIntervalFrom: defaults.from,
            dateIntervalTo: defaults.to,
          };
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Не удалось загрузить фильтры");
      })
      .finally(() => {
        if (isMounted) setOptionsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (!selectedGroupId) return;
    let isMounted = true;
    setOptionsLoading(true);
    setSelectedHotelHasCompetitors(isDemoAnalytics ? true : null);

    getFilters(selectedGroupId)
      .then((filtersResponse) => {
        if (!isMounted) return;
        const mapOptions = (
          items?: Array<{ key: string; value: string } | string> | Record<string, string>,
        ) => {
          if (!items) return [];
          if (!Array.isArray(items)) {
            return Object.entries(items).map(([key, value]) => ({ label: value, value: key }));
          }
          return items
            .map((item) => {
              if (typeof item === "string") return { label: item, value: item };
              if (!item) return null;
              const value = item.key ?? item.value;
              const label = item.value ?? item.key;
              if (!value || !label) return null;
              return { label, value };
            })
            .filter((item): item is { label: string; value: string } => Boolean(item));
        };
        const mapCompetitorOptions = (
          items?: Array<{ hotel_id: string | number; hotel_name: string; is_base: boolean | 0 | 1 | "0" | "1" } | { key: string; value: string } | string> | Record<string, string>,
        ) => {
          if (!items) {
            setBaseHotelFilterNames([]);
            setCompetitorHotelNameMap({});
            return [];
          }
          if (!Array.isArray(items)) {
            setBaseHotelFilterNames([]);
            setCompetitorHotelNameMap({});
            return mapOptions(items);
          }

          const baseNames: string[] = [];
          const nextCompetitorNameMap: Record<string, string> = {};
          const competitors = items
            .map((item) => {
              if (typeof item === "string") return { label: item, value: item };
              if (!item) return null;
              if ("hotel_id" in item && "hotel_name" in item) {
                const isBase = item.is_base === true || item.is_base === 1 || item.is_base === "1";
                if (isBase) {
                  baseNames.push(item.hotel_name);
                  return null;
                }
                nextCompetitorNameMap[String(item.hotel_id)] = item.hotel_name;
                return { label: item.hotel_name, value: String(item.hotel_id) };
              }
              const value = item.key ?? item.value;
              const label = item.value ?? item.key;
              if (!value || !label) return null;
              return { label, value };
            })
            .filter((item): item is { label: string; value: string } => Boolean(item));

          setBaseHotelFilterNames(baseNames);
          setCompetitorHotelNameMap(nextCompetitorNameMap);
          return competitors;
        };

        const competitorOptions = mapCompetitorOptions(filtersResponse.hotel);
        setSelectedHotelHasCompetitors(isDemoAnalytics || competitorOptions.length > 0);
        setFilterOptions({
          dataSource: mapOptions(filtersResponse.data_source),
          room: mapOptions(filtersResponse.room),
          category: mapOptions(filtersResponse.category),
          ota: mapOptions(filtersResponse.ota),
          meals: mapOptions(filtersResponse.board_type),
          refund: mapOptions(filtersResponse.refund_policy),
          payment: mapOptions(filtersResponse.payment_type),
          competitors: competitorOptions,
        });
        const nextRange = filtersResponse.check_in_dates ?? null;
        setCheckInRange(nextRange);
        const defaults = getDefaultInterval();
        setFilters((prev) => ({
          ...prev,
          date: prev.date || getDefaultDate(),
          dateIntervalFrom: prev.dateIntervalFrom || defaults.from,
          dateIntervalTo: prev.dateIntervalTo || defaults.to,
        }));
        if (!filters.dateIntervalFrom && !filters.dateIntervalTo) {
          lastDefaultIntervalRef.current = defaults;
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setSelectedHotelHasCompetitors(null);
        setError("Не удалось загрузить фильтры");
      })
      .finally(() => {
        if (isMounted) setOptionsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isDemoAnalytics, selectedGroupId]);

  useEffect(() => {
    if (!selectedHotelMinStay?.length) {
      setDaysStay(1);
      return;
    }
    if (!selectedHotelMinStay.includes(daysStay)) {
      setDaysStay(selectedHotelMinStay[0]);
    }
  }, [daysStay, selectedHotelMinStay]);

  useEffect(() => {
    if (!selectedHotelGuestOptions.some((option) => option.value === guests)) {
      setGuests(selectedHotelGuestOptions[0]?.value ?? "2");
    }
  }, [guests, selectedHotelGuestOptions]);

  useEffect(() => {
    const lastDate = lastParsingDate.date;
    if (!lastDate) return;
    const nextInterval = getDefaultInterval(lastDate);
    const currentDefaultInterval = lastDefaultIntervalRef.current;

    setFilters((prev) => {
      if (!prev.hotel) return prev;
      const shouldApplyDefault =
        (!prev.dateIntervalFrom && !prev.dateIntervalTo) ||
        Boolean(
          currentDefaultInterval &&
            prev.dateIntervalFrom === currentDefaultInterval.from &&
            prev.dateIntervalTo === currentDefaultInterval.to,
        );
      if (
        !shouldApplyDefault ||
        (prev.dateIntervalFrom === nextInterval.from && prev.dateIntervalTo === nextInterval.to)
      ) {
        return prev;
      }
      return { ...prev, dateIntervalFrom: nextInterval.from, dateIntervalTo: nextInterval.to };
    });
    lastDefaultIntervalRef.current = nextInterval;
  }, [lastParsingDate.date]);

  useEffect(() => {
    if (!selectedGroupId || !isAvailableParsingDatesReady || availableParsingDates.error) return;
    setFilters((prev) => {
      if (!prev.hotel) return prev;
      const nextRange = normalizeAvailableDateRange(
        prev.dateIntervalFrom,
        prev.dateIntervalTo,
        defaultCollectionInterval.from,
        defaultCollectionInterval.to,
        availableParsingDates.dates,
      );
      if (prev.dateIntervalFrom === nextRange.from && prev.dateIntervalTo === nextRange.to) return prev;
      return { ...prev, dateIntervalFrom: nextRange.from, dateIntervalTo: nextRange.to };
    });
  }, [
    availableParsingDates.dates,
    availableParsingDates.datesKey,
    availableParsingDates.error,
    defaultCollectionInterval.from,
    defaultCollectionInterval.to,
    isAvailableParsingDatesReady,
    selectedGroupId,
  ]);

  useEffect(() => {
    if (!hasHotelSelected) {
      setNoData(false);
      setSelectedHotelHasCompetitors(null);
    }
  }, [hasHotelSelected]);

  useEffect(() => {
    if (!hasHotelSelected || shouldBlockAnalyticsRequests) {
      setMinDynamics({ dates: [], rows: [] });
      setAvgDynamics({ dates: [], rows: [] });
    }
  }, [hasHotelSelected, shouldBlockAnalyticsRequests]);

  const mapDynamics = (resp: {
    hotels: Array<{ hotel_id: string | number; hotel_name: string; is_base: 0 | 1 }>;
    dates: Record<string, Record<string, number | null>>;
  }) => {
    const dateKeys = Object.keys(resp.dates || {});
    const sortedDates = dateKeys.sort((a, b) => a.localeCompare(b));
    const rows = resp.hotels.map((hotel) => ({
      name: hotel.hotel_name,
      isClient: String(hotel.is_base) === "1",
      values: sortedDates.map((dateKey) => {
        const values = resp.dates[dateKey] || {};
        return values[String(hotel.hotel_id)] ?? null;
      }),
    }));
    rows.sort((a, b) => Number(Boolean(b.isClient)) - Number(Boolean(a.isClient)));
    return { dates: sortedDates.map(formatDateShort), rows };
  };

  const fetchDynamics = async (isAvg: boolean, nextFilters?: typeof filters) => {
    if (isSubscriptionExpired) {
      setLoading(false);
      setNoData(false);
      setError(null);
      setMinDynamics({ dates: [], rows: [] });
      setAvgDynamics({ dates: [], rows: [] });
      return;
    }
    const currentFilters = nextFilters ?? filters;
    if (shouldBlockAnalyticsRequests) {
      setLoading(false);
      setNoData(false);
      setError(null);
      setMinDynamics({ dates: [], rows: [] });
      setAvgDynamics({ dates: [], rows: [] });
      return;
    }
    const intervalDefaults = defaultCollectionInterval;
    const interval = {
      from: currentFilters.dateIntervalFrom || intervalDefaults.from,
      to: currentFilters.dateIntervalTo || intervalDefaults.to,
    };
    if (
      !availableParsingDates.dateSet.has(interval.from) ||
      !availableParsingDates.dateSet.has(interval.to) ||
      interval.to < interval.from
    ) {
      setLoading(false);
      setNoData(false);
      if (isAvg) {
        setAvgDynamics({ dates: [], rows: [] });
      } else {
        setMinDynamics({ dates: [], rows: [] });
      }
      return;
    }
    const requestRef = isAvg ? avgDynamicsRequestIdRef : minDynamicsRequestIdRef;
    const requestId = ++requestRef.current;
    setLoading(true);
    setNoData(false);
    setError(null);
    if (isAvg) {
      setAvgDynamics({ dates: [], rows: [] });
    } else {
      setMinDynamics({ dates: [], rows: [] });
    }

    try {
      if (!selectedGroupId) return;
      const payload = {
        group_id: selectedGroupId,
        ...buildBaseFiltersPayload(currentFilters),
        ...buildDaysStayPayload(),
        ...buildGuestsPayload(),
        hotel: buildHotelFilterPayload(currentFilters),
        date: currentFilters.date || getDefaultDate(),
        date_interval: interval,
        is_avg: isAvg,
      };
      const response = await getPriceDynamics(payload);
      if (requestId !== requestRef.current) return;
      const mapped = mapDynamics(response);
      setNoData(mapped.dates.length === 0 || mapped.rows.length === 0);
      if (isAvg) {
        setAvgDynamics(mapped);
      } else {
        setMinDynamics(mapped);
      }
    } catch {
      if (requestId !== requestRef.current) return;
      setNoData(false);
      setError("Не удалось загрузить динамику цен");
      if (isAvg) {
        setAvgDynamics({ dates: [], rows: [] });
      } else {
        setMinDynamics({ dates: [], rows: [] });
      }
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (
      isSubscriptionExpired ||
      !hasHotelSelected ||
      !selectedGroupId ||
      shouldBlockAnalyticsRequests ||
      shouldBlockParsingDateRequests ||
      !isLastParsingDateReady ||
      !isAvailableParsingDatesReady ||
      shouldWaitForDefaultInterval
    ) return;
    if (activeTab === "min") {
      fetchDynamics(false);
    } else {
      fetchDynamics(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, activeTab, daysStay, guests, isSubscriptionExpired, shouldBlockAnalyticsRequests, shouldBlockParsingDateRequests, isLastParsingDateReady, isAvailableParsingDatesReady, shouldWaitForDefaultInterval]);

  const handleApply = async () => {
    if (loading || isSubscriptionExpired || shouldBlockAnalyticsRequests || shouldBlockParsingDateRequests || !isLastParsingDateReady || !isAvailableParsingDatesReady) return;
    setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
    await fetchDynamics(activeTab === "avg");
  };

  const handleReset = async () => {
    if (loading || isSubscriptionExpired || shouldBlockAnalyticsRequests || !isLastParsingDateReady || !isAvailableParsingDatesReady || availableParsingDates.error) return;
    const defaults = defaultCollectionInterval;
    const resetRange = normalizeAvailableDateRange(
      defaults.from,
      defaults.to,
      defaults.from,
      defaults.to,
      availableParsingDates.dates,
    );
    const resetFilters = {
      ...DEFAULT_FILTERS,
      hotel: filters.hotel,
      date: getDefaultDate(),
      dateIntervalFrom: resetRange.from,
      dateIntervalTo: resetRange.to,
    };
    lastDefaultIntervalRef.current = resetRange;
    setFilters(resetFilters);
    setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
    await fetchDynamics(activeTab === "avg", resetFilters);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="px-6 py-8">
        <div className="mx-auto flex max-w-[1280px] flex-col gap-6">
          <section className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="pointer-events-none absolute inset-y-0 right-0 w-40 translate-x-10 rounded-full bg-gradient-to-b from-indigo-100 to-purple-100 blur-3xl opacity-60" />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="mt-2 text-3xl font-bold text-slate-900">Тренды по конкурентам</h1>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="flex flex-col gap-3 self-start">
                  {hasHotelSelected && hasSelectedHotelMinStay && (
                    <div className="flex items-center gap-3 self-start sm:self-auto">
                    <span className="text-xs font-semibold uppercase tracking-[0.08em] text-indigo-500">Срок заселения</span>
                    <div className="inline-flex rounded-full border border-indigo-200 bg-white p-1 shadow-sm">
                      {selectedHotelMinStayOptions.map((value) => {
                        const isActive = daysStay === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => {
                              setDaysStay(value);
                              setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
                            }}
                            className={[
                              "rounded-full px-3 py-1 text-xs font-semibold transition",
                              isActive ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-50",
                            ].join(" ")}
                            aria-pressed={isActive}
                          >
                            {formatDaysStayLabel(value)}
                          </button>
                        );
                      })}
                    </div>
                    </div>
                  )}
                  {hasHotelSelected && hasSelectedHotelGuests && (
                    <GuestCountDisplay value={guests} options={selectedHotelGuestOptions} onChange={setGuests} />
                  )}
                </div>
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-5 py-4 text-sm text-indigo-700">
                  {lastParsingDate.display ? `${LAST_PARSING_DATE_LABEL}: ${lastParsingDate.display}` : appliedAt
                    ? `Фильтры обновлены в ${appliedAt}`
                    : "Выберите объект, задайте фильтры и нажмите «Применить»"}
                </div>
              </div>
            </div>
          </section>

          {error && <DataUnavailableCard message={TECHNICAL_ERROR_MESSAGE} variant="error" />}

          {isDemoAnalytics && <DemoAnalyticsBanner />}

          <ObjectSelectCard
            value={filters.hotel}
            options={hotelOptions}
            onChange={(value) => {
              setNoData(false);
              setSelectedHotelHasCompetitors(value === DEMO_ANALYTICS_OBJECT_VALUE ? true : null);
              const defaults = getDefaultInterval();
              lastDefaultIntervalRef.current = defaults;
              setFilters({
                ...DEFAULT_FILTERS,
                hotel: value,
                date: getDefaultDate(),
                dateIntervalFrom: defaults.from,
                dateIntervalTo: defaults.to,
              });
            }}
            loading={optionsLoading}
          />

          <section className="rounded-3xl border border-slate-200 bg-white/90 p-2 shadow-[0_10px_24px_rgba(15,23,42,0.06)]">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {[
                { id: "min", label: "Тренды минимальной цены" },
                { id: "avg", label: "Тренды по средним ценам" },
              ].map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id as "min" | "avg")}
                    className={[
                      "w-full rounded-2xl px-4 py-2 text-sm font-semibold transition",
                      isActive
                        ? "bg-indigo-600 text-white shadow-[0_10px_24px_rgba(79,70,229,0.35)]"
                        : "text-slate-500 hover:bg-slate-100",
                    ].join(" ")}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </section>

          {hasHotelSelected && filtersLoading ? (
            <FiltersLoadingPlaceholder label="Загружаем фильтры и доступные даты..." />
          ) : hasHotelSelected && (
            <FiltersSection
              filters={filters}
              options={filterOptions}
              onChange={setFilters}
              onApply={handleApply}
              onReset={handleReset}
              checkInRange={checkInRange}
              showCompetitors={false}
              showDateInterval
              loading={loading || optionsLoading || !isLastParsingDateReady || shouldBlockAnalyticsRequests}
              applyDisabled={shouldBlockParsingDateRequests}
              parsingDateAvailability={{
                dates: availableParsingDates.dates,
                loading: availableParsingDates.loading,
                error: availableParsingDates.error,
                restrictDateInterval: true,
              }}
            />
          )}

          {!hasHotelSelected && (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/90 px-6 py-10 text-center text-slate-500">
              Выберите объект размещения, чтобы загрузить тренды.
            </div>
          )}

          {hasHotelSelected && !error && optionsLoading && (
            <section className="relative min-h-[160px] overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
              <LoadingOverlay visible label="Загружаем данные..." />
            </section>
          )}

          {hasHotelSelected && !isSubscriptionExpired && !error && !optionsLoading && loading && (selectedHotelHasCompetitors !== true || noData) && (
            <section className="relative min-h-[220px] overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
              <LoadingOverlay visible label="Загружаем данные..." />
            </section>
          )}

          {hasHotelSelected && !isSubscriptionExpired && !error && !loading && selectedHotelHasCompetitors === false && (
            <DataUnavailableCard
              message={
                <>
                  Для начала работы добавьте конкурентов в{" "}
                  <Link className="text-indigo-600 underline decoration-indigo-300 underline-offset-4 hover:text-indigo-700" to={`/my/objects/${filters.hotel}`}>
                    объект размещения
                  </Link>
                </>
              }
            />
          )}

          {hasHotelSelected && !isSubscriptionExpired && !error && !loading && selectedHotelHasCompetitors === true && noData && <DataUnavailableCard />}

          {hasHotelSelected && !isSubscriptionExpired && !error && selectedHotelHasCompetitors === true && !noData && (
            <section className="relative">
              <LoadingOverlay visible={loading} label="Загружаем тренды..." />
              <div className={loading ? "pointer-events-none opacity-60" : ""}>
                {activeTab === "min" && (
                  <>
                    <DynamicsChart title="Тренды минимальной цены" data={minDynamics} yLabel="Минимальная цена" />
                    <div className="mt-6">
                      <DynamicsTableCard title="Тренды минимальной цены" data={minDynamics} fullCellHeatmap alignRight />
                    </div>
                  </>
                )}

                {activeTab === "avg" && (
                  <>
                    <DynamicsChart title="Тренды по средним ценам" data={avgDynamics} yLabel="Средняя цена" />
                    <div className="mt-6">
                      <DynamicsTableCard title="Тренды по средним ценам" data={avgDynamics} fullCellHeatmap alignRight />
                    </div>
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
